/**
 * EmailThreadCollab.jsx — Wave 1u3 Phase C / J1 + J2 + J4
 *
 * Collaboration panel rendered inside the email viewer. Combines:
 *   • J4 — Kanban status pill (to_handle / in_progress / done / archived)
 *   • J2 — Assignee picker (any user in the same org)
 *   • J1 — Internal comments timeline (separate from the email itself)
 *
 * All endpoints are scoped per (org_id, thread_id) on the server.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, User as UserIcon, Loader2, Send, Trash2 } from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';

const STATUS_OPTIONS = [
  { value: 'to_handle', label: 'À traiter', bg: '#FEF3C7', fg: '#92400E', br: '#FDE68A' },
  { value: 'in_progress', label: 'En cours', bg: '#DBEAFE', fg: '#1E40AF', br: '#93C5FD' },
  { value: 'done', label: 'Traité', bg: '#DCFCE7', fg: '#166534', br: '#86EFAC' },
  { value: 'archived', label: 'Archivé', bg: '#F3F4F6', fg: '#4B5563', br: '#D1D5DB' },
];

function StatusBadge({ value }) {
  const s = STATUS_OPTIONS.find((o) => o.value === value) || STATUS_OPTIONS[0];
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold border"
      style={{ backgroundColor: s.bg, color: s.fg, borderColor: s.br }}
    >
      {s.label}
    </span>
  );
}

export default function EmailThreadCollab({ threadId, onChange }) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('to_handle');
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [assigneeName, setAssigneeName] = useState('');
  const [comments, setComments] = useState([]);
  const [team, setTeam] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [meUserId, setMeUserId] = useState(null);
  const [meRole, setMeRole] = useState('');

  const refreshSummaryHint = useCallback((next) => {
    onChange?.({
      status: next.status ?? status,
      assignee_user_id: next.assignee_user_id ?? assigneeUserId,
      assignee_name: next.assignee_name ?? assigneeName,
      comments_count: next.comments_count ?? comments.length,
    });
  }, [onChange, status, assigneeUserId, assigneeName, comments.length]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/mailbox/threads/${threadId}/collab`),
      api.get('/mailbox/team'),
      api.get('/auth/me').catch(() => ({ data: null })),
    ])
      .then(([{ data: collab }, { data: teamRes }, { data: meRes }]) => {
        if (!alive) return;
        setStatus(collab?.status || 'to_handle');
        setAssigneeUserId(collab?.assignee_user_id || '');
        setAssigneeName(collab?.assignee_name || '');
        setComments(Array.isArray(collab?.comments) ? collab.comments : []);
        setTeam(Array.isArray(teamRes?.members) ? teamRes.members : []);
        setMeUserId(meRes?.user_id || null);
        setMeRole(meRes?.role || '');
      })
      .catch(() => alive && setTeam([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [threadId]);

  const isAdmin = ['administrateur', 'admin', 'owner', 'super_admin'].includes((meRole || '').toLowerCase());

  const updateStatus = async (nextStatus) => {
    try {
      const { data } = await api.put(`/mailbox/threads/${threadId}/collab`, { status: nextStatus });
      setStatus(data?.status || nextStatus);
      refreshSummaryHint({ status: data?.status });
      toast({ title: 'Statut mis à jour' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  const updateAssignee = async (nextUserId) => {
    try {
      const { data } = await api.put(`/mailbox/threads/${threadId}/collab`, { assignee_user_id: nextUserId });
      setAssigneeUserId(data?.assignee_user_id || '');
      setAssigneeName(data?.assignee_name || '');
      refreshSummaryHint({ assignee_user_id: data?.assignee_user_id, assignee_name: data?.assignee_name });
      toast({ title: data?.assignee_user_id ? 'Assigné' : 'Désassigné' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  const postComment = async () => {
    const body = newComment.trim();
    if (!body || posting) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/mailbox/threads/${threadId}/comments`, { body });
      setComments((prev) => [...prev, data]);
      setNewComment('');
      refreshSummaryHint({ comments_count: comments.length + 1 });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async (c) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    try {
      await api.delete(`/mailbox/threads/${threadId}/comments/${c.id}`);
      setComments((prev) => prev.filter((x) => x.id !== c.id));
      refreshSummaryHint({ comments_count: comments.length - 1 });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  if (loading) {
    return (
      <div className="border-t border-[#EFF0F6] px-6 py-3 text-[#8E8FA5] text-[12.5px]" data-testid="email-collab-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin inline-block mr-1" /> Chargement collaboration…
      </div>
    );
  }

  return (
    <div className="border-t border-[#EFF0F6] bg-[#FAFAFE]" data-testid="email-collab-panel">
      {/* Status + Assignee row */}
      <div className="px-6 py-2.5 flex items-center gap-4 flex-wrap border-b border-[#EFF0F6]">
        <div className="flex items-center gap-2" data-testid="email-collab-status">
          <span className="text-[11px] font-semibold text-[#6B6C85]">Statut</span>
          <select
            data-testid="email-collab-status-select"
            value={status}
            onChange={(e) => updateStatus(e.target.value)}
            className="h-7 px-2 text-[12px] bg-white border border-[#EFF0F6] rounded focus:outline-none focus:border-[#4F46E5]"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <StatusBadge value={status} />
        </div>
        <div className="flex items-center gap-2" data-testid="email-collab-assignee">
          <span className="text-[11px] font-semibold text-[#6B6C85]">Assigné à</span>
          <select
            data-testid="email-collab-assignee-select"
            value={assigneeUserId}
            onChange={(e) => updateAssignee(e.target.value)}
            className="h-7 px-2 text-[12px] bg-white border border-[#EFF0F6] rounded focus:outline-none focus:border-[#4F46E5]"
          >
            <option value="">— Personne —</option>
            {team.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.name || m.email || m.user_id}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Comments timeline */}
      <div className="px-6 py-3" data-testid="email-collab-comments">
        <div className="text-[11px] font-semibold text-[#6B6C85] mb-2 flex items-center gap-1">
          <MessageSquare className="w-3 h-3" />
          Commentaires internes ({comments.length})
        </div>
        {comments.length === 0 ? (
          <div className="text-[12px] text-[#8E8FA5] italic mb-2">
            Aucun commentaire interne. Ajoutez-en un ci-dessous pour le partager avec votre équipe.
          </div>
        ) : (
          <ol className="space-y-2 mb-3">
            {comments.map((c) => {
              const canDelete = isAdmin || c.author_user_id === meUserId;
              const ts = c.created_at ? new Date(c.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '';
              return (
                <li
                  key={c.id}
                  data-testid={`email-collab-comment-${c.id}`}
                  className="p-2.5 rounded-lg bg-white border border-[#EFF0F6] flex items-start gap-2"
                >
                  <div className="w-6 h-6 rounded-full bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center flex-shrink-0">
                    <UserIcon className="w-3 h-3" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[12px] font-semibold text-[#1A1B2E]">{c.author_name}</span>
                      <span className="text-[10.5px] text-[#8E8FA5]">{ts}</span>
                    </div>
                    <div className="text-[12.5px] text-[#1A1B2E] mt-0.5 whitespace-pre-wrap break-words">{c.body}</div>
                  </div>
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => deleteComment(c)}
                      data-testid={`email-collab-comment-delete-${c.id}`}
                      className="text-[#B91C1C] hover:bg-[#FEF2F2] h-6 w-6 rounded inline-flex items-center justify-center flex-shrink-0"
                      title="Supprimer"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </li>
              );
            })}
          </ol>
        )}
        <div className="flex items-end gap-2">
          <textarea
            data-testid="email-collab-comment-input"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                postComment();
              }
            }}
            placeholder="Ajouter un commentaire interne… (Ctrl+Entrée pour envoyer)"
            rows={2}
            className="flex-1 px-2 py-1.5 text-[12.5px] bg-white border border-[#EFF0F6] rounded focus:outline-none focus:border-[#4F46E5] resize-none"
          />
          <button
            type="button"
            onClick={postComment}
            disabled={posting || !newComment.trim()}
            data-testid="email-collab-comment-submit"
            className="h-9 px-3 rounded-lg brand-gradient text-white text-[12px] font-semibold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Publier
          </button>
        </div>
      </div>
    </div>
  );
}
